import { useState } from "react";
import {
  DropDownList,
  DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { accessibleNoDataRender } from "@/components/shared/forms/kendoAccessibleNoData";

const ALL_SUBMISSION_TYPES = "All Submission Types";
const ALL_PROGRAM_YEARS = "All Program Years";
const ALL_CERTIFICATION_STATUSES = "All Certification Statuses";

export type CertificationSearchFormState = {
  submissionType: string;
  programYear: string;
  certificationStatus: string;
};

const emptyFilters: CertificationSearchFormState = {
  submissionType: "",
  programYear: "",
  certificationStatus: "",
};

type CertificationSearchFormProps = {
  submissionTypeOptions: string[];
  programYearOptions: string[];
  certificationStatusOptions: string[];
  disabled: boolean;
  onSearch: (filters: CertificationSearchFormState) => void;
  onClear?: () => void;
};

export function CertificationSearchForm({
  submissionTypeOptions,
  programYearOptions,
  certificationStatusOptions,
  disabled,
  onSearch,
  onClear,
}: CertificationSearchFormProps) {
  const [formState, setFormState] =
    useState<CertificationSearchFormState>(emptyFilters);

  const handleChange = (
    key: keyof CertificationSearchFormState,
    value: string
  ) => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyClick = () => {
    if (!disabled) onSearch(formState);
  };

  const handleClearClick = () => {
    setFormState(emptyFilters);

    if (!disabled) onSearch(emptyFilters);

    onClear?.();
  };

  return (
    <div className="mb-0 p-4 bg-white w-100 child-search-form">
      <div className="row g-3">
        {/* Program Year */}
        <div className="col-12 col-md-4">
          <span
            id="certification-search-program-year-label"
            className="form-label fw-semibold"
          >
            <i className="bi bi-calendar me-1" aria-hidden="true"></i> Program
            Year
          </span>
          <DropDownList
            id="certification-search-program-year"
            ariaLabelledBy="certification-search-program-year-label"
            className="child-search-control"
            style={{ width: "100%" }}
            data={programYearOptions}
            defaultItem={ALL_PROGRAM_YEARS}
            value={formState.programYear || ALL_PROGRAM_YEARS}
            onChange={(e: DropDownListChangeEvent) => {
              if (!e.value) return;
              handleChange(
                "programYear",
                e.value === ALL_PROGRAM_YEARS ? "" : e.value
              );
            }}
            disabled={disabled}
            listNoDataRender={accessibleNoDataRender(
              "No program years available"
            )}
          />
        </div>

        {/* Submission Type */}
        <div className="col-12 col-md-4">
          <span
            id="certification-search-submission-type-label"
            className="form-label fw-semibold"
          >
            <i className="bi bi-list-check me-1" aria-hidden="true"></i>{" "}
            Submission Type
          </span>
          <DropDownList
            id="certification-search-submission-type"
            ariaLabelledBy="certification-search-submission-type-label"
            className="child-search-control"
            style={{ width: "100%" }}
            data={submissionTypeOptions}
            defaultItem={ALL_SUBMISSION_TYPES}
            value={formState.submissionType || ALL_SUBMISSION_TYPES}
            onChange={(e: DropDownListChangeEvent) => {
              if (!e.value) return;
              handleChange(
                "submissionType",
                e.value === ALL_SUBMISSION_TYPES ? "" : e.value
              );
            }}
            disabled={disabled}
            listNoDataRender={accessibleNoDataRender(
              "No submission types available"
            )}
          />
        </div>

        {/* Certification Status */}
        <div className="col-12 col-md-4">
          <span
            id="certification-search-status-label"
            className="form-label fw-semibold"
          >
            <i className="bi bi-check-circle me-1" aria-hidden="true"></i>{" "}
            Certification Status
          </span>
          <DropDownList
            id="certification-search-status"
            ariaLabelledBy="certification-search-status-label"
            className="child-search-control"
            style={{ width: "100%" }}
            data={certificationStatusOptions}
            defaultItem={ALL_CERTIFICATION_STATUSES}
            value={formState.certificationStatus || ALL_CERTIFICATION_STATUSES}
            onChange={(e: DropDownListChangeEvent) => {
              if (!e.value) return;
              handleChange(
                "certificationStatus",
                e.value === ALL_CERTIFICATION_STATUSES ? "" : e.value
              );
            }}
            disabled={disabled}
            listNoDataRender={accessibleNoDataRender(
              "No certification statuses available"
            )}
          />
        </div>

        {/* Buttons */}
        <div className="col-12 d-flex align-items-end gap-2 mt-4 mb-1">
          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={handleApplyClick}
            disabled={disabled}
          >
            <i className="bi bi-funnel" aria-hidden="true"></i>
            Apply Filters
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClearClick}
            disabled={disabled}
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
}
